import React, { useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import BPEdit_Breakpoint from "./bpedit_breakpoint"
import BPEdit_Watchpoint from "./bpedit_watchpoint"
import BPEdit_Instruction from "./bpedit_instruction"
import BPEdit_Basic from "./bpedit_basic"
import { BRK_NOADDRESS, getBreakpointKey } from "../../../common/breakpoint"
import { useTranslation } from "../../../i18n/useTranslation"

const BreakpointEdit = (props: {
  breakpoint: Breakpoint,
  setBreakpoint: (bp: Breakpoint) => void,
  saveBreakpoint: () => void,
  cancelDialog: () => void,
  dialogPositionX: number,
  dialogPositionY: number,
  setDialogPosition: (x: number, y: number) => void
}) => {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState([0, 0])
  const [dragging, setDragging] = useState(false)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (dialogRef.current) {
      setDragging(true)
      const div = dialogRef.current as HTMLDivElement
      // Offset of the mouse down event within the dialog title bar.
      // This way we drag the window from where the user clicked.
      setOffset([e.clientX - div.offsetLeft, e.clientY - div.offsetTop])
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (dragging && dialogRef.current) {
      const div = dialogRef.current as HTMLDivElement
      const left = e.clientX - offset[0]
      const top = e.clientY - offset[1]
      props.setDialogPosition(left, top)
      div.style.left = `${left}px`
      div.style.top = `${top}px`
    }
  }

  const handleMouseUp = () => {
    setDragging(false)
  }

  const handleBreakAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isInstruction = e.target.value === "instruction"
    props.setBreakpoint({
      ...props.breakpoint,
      watchpoint: e.target.value === "watchpoint",
      instruction: isInstruction,
      basic: e.target.value === "basic",
      address: isInstruction ? 0 : props.breakpoint.address,
    })
  }

  const isBreakpoint = !props.breakpoint.watchpoint &&
    !props.breakpoint.instruction && !props.breakpoint.basic

  const radioBreakpoint =
    <>
      <input type="radio"
        id="Address"
        name="breakAt"
        value="address"
        autoComplete="off"
        className="check-radio-box"
        checked={isBreakpoint}
        onChange={(e) => { handleBreakAtChange(e) }} />
      <label htmlFor="Address" className="dialog-text flush-left">{t("debug.breakpoint.breakpoint")}</label>
    </>

  const radioWatchpoint =
    <>
      <input type="radio"
        id="Watchpoint"
        name="watch"
        value="watchpoint"
        autoComplete="off"
        className="check-radio-box"
        checked={props.breakpoint.watchpoint}
        onChange={(e) => { handleBreakAtChange(e) }} />
      <label htmlFor="Watchpoint" className="dialog-text flush-left">{t("debug.breakpoint.watchpoint")}</label>
    </>

  const radioInstruction =
    <>
      <input type="radio"
        id="Instruction"
        name="instruction"
        value="instruction"
        autoComplete="off"
        className="check-radio-box"
        checked={props.breakpoint.instruction}
        onChange={(e) => { handleBreakAtChange(e) }} />
      <label htmlFor="Instruction" className="dialog-text flush-left">{t("debug.breakpoint.instruction")}</label>
    </>

  const radioBASIC =
    <>
      <input type="radio"
        id="Basic"
        name="basic"
        value="basic"
        autoComplete="off"
        className="check-radio-box"
        checked={props.breakpoint.basic}
        onChange={(e) => { handleBreakAtChange(e) }} />
      <label htmlFor="Basic" className="dialog-text flush-left">BASIC</label>
    </>

  return (
    <div className="modal-overlay"
      tabIndex={0} // Make the div focusable
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          props.cancelDialog()
        } else if (event.key === "Enter") {
          props.saveBreakpoint()
        }
      }}
      onMouseMove={(e) => handleMouseMove(e)}
      onMouseUp={handleMouseUp}>
      <div className="floating-dialog flex-column-gap"
        ref={dialogRef}
        style={{
          left: `${props.dialogPositionX}px`,
          top: `${props.dialogPositionY}px`,
          width: "550px",
          height: "auto",
          minHeight: "400px",
          userSelect: "none"
        }}
      >
        <div className="flex-column">
          <div className="flex-row-space-between"
            onMouseDown={(e) => handleMouseDown(e)}
            onMouseMove={(e) => handleMouseMove(e)}
            onMouseUp={handleMouseUp}>
            <div className="dialog-title">{t("debug.breakpointTitle")}</div>
            <button className="push-button"
              onClick={props.cancelDialog}>
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: "0.8em" }} />
            </button>
          </div>
          <div className="horiz-rule"></div>
          <div className="flex-row" style={{ alignItems: "center" }}>
            <div className="dialog-text">{t("debug.breakpoint.breakAt")}</div>
            {radioBreakpoint}
            {radioWatchpoint}
            {radioInstruction}
            {radioBASIC}
          </div>
          <div className="horiz-rule" style={{ marginTop: "10px" }}/>
          {isBreakpoint && <BPEdit_Breakpoint breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint} />}
          {props.breakpoint.watchpoint && <BPEdit_Watchpoint
            breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint} />}
          {props.breakpoint.instruction && <BPEdit_Instruction
            breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint} />}
          {props.breakpoint.basic && <BPEdit_Basic breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint} />}
        </div>
        <div id="button-row" className="flex-row-space-between" style={{ marginTop: "auto" }}>
          <div></div>
          <div className="flex-row">
            <button className="push-button text-button"
              disabled={isBreakpoint && getBreakpointKey(props.breakpoint) === BRK_NOADDRESS}
              onClick={props.saveBreakpoint}>
              <span className="centered-title">OK</span>
            </button>
            <button className="push-button text-button"
              onClick={props.cancelDialog}>
              <span className="centered-title">{t("messages.cancel")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BreakpointEdit
